import * as React from 'react';
import Box from '@mui/material/Box';
import '../../styles/ModalStylesheet.scss';
import {Dialog, IconButton, LinearProgress, Stack, useTheme} from "@mui/material";
import {Close} from "@carbon/icons-react";
import {ModalWrapperProps} from "../../common/interfaces/modalWrapperProps.interface";
import {useIsMobile} from "../../common/hooks/useIsMobile";
import {CSSProperties, useEffect, useMemo, useRef, useState} from "react";
import moment from "moment";

const ModalWrapper = (props: ModalWrapperProps) => {
    const theme = useTheme();
    
    const {
        children,
        openModal=false,
        onClose,
        color,
        closeIconColor = theme.palette.thatsMe.iconsButton, 
        closeIconBackgroundColor = theme.palette.thatsMe.surfacePrimary, 
        hideCloseIcon,
        hasProgress,
        progress,
        progressColor,
        modalFixedHeight,
        modalFixedWidth,
        modalContentWrapperStyle,
        onBackgroundTransitionEnd,
        animateBackground
    } = props;
    
    const isMobile = useIsMobile();
    const [isModalScrollable, setIsModalScrollable] = useState(false);
    const [isModalTransitionEnded, setIsModalTransitionEnded] = useState(false);
    const [isBackgroundTransitionEnded, setIsBackgroundTransitionEnded] = useState(false);
    const duration = 300;
    const defaultStyle = useMemo(() => {
        return {
            transition: theme.transitions.create(['background-position'], { duration, delay: 500, easing: 'linear'})
        };
    }, []);
    const [startTime, setStartTime] = useState(moment(Date.now()).add(duration));

    const modalRef = useRef<HTMLDivElement>(null);
    const closeIconStyles = useMemo(() => {
        const style: CSSProperties = {};

        style.border = "1px solid transparent";
        
        if(isModalScrollable) {
            style.backgroundColor = closeIconBackgroundColor;
            style.border = `1px solid ${theme.palette.grey["200"]}`;
        }
        
        if(hideCloseIcon) {
            style.opacity = 0;
            style.pointerEvents = "none";
        }

        return style;
    }, [hideCloseIcon, isModalScrollable, theme]);
    
    const setHeight = () => {
        
        if(modalFixedHeight) {
            return modalFixedHeight;
        }
        
        if(isMobile) {
            return '100%';
        }
        
        return `calc(100% - ${theme.thatsMe.MuiToolbar.minHeight}`;
    };

    const setWidth = () => {
        
        if(modalFixedWidth) {
            return modalFixedWidth;
        }
        
        if(isMobile) {
            return '100%';
        }

        return `400px`;
    };

    useEffect(() => {
        const handleResize = () => {
            if(!modalRef.current)
            {
                return;
            }

            setIsModalScrollable(modalRef.current.clientHeight < modalRef.current.scrollHeight);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if(!modalRef.current)
        {
            return;
        }

        setIsModalScrollable(modalRef.current.clientHeight < modalRef.current.scrollHeight);
    }, [isModalTransitionEnded]);

    useEffect(() => {
        setStartTime(moment(Date.now()).add(duration));
    }, [isModalTransitionEnded, color, animateBackground]);

    useEffect(() => {
        setIsBackgroundTransitionEnded(false);
    }, [color]);

    useEffect(() => {
        if(openModal) {
            return;
        }

        setIsModalTransitionEnded(false);
        setIsBackgroundTransitionEnded(false);
    }, [openModal]);

    return (
        <Dialog
            open={openModal}
            onClose={onClose}
            className="modal-box"
            onTransitionEnd={() => { setIsModalTransitionEnded(true);}}
            PaperProps={
                {
                    ref: modalRef,
                    style: {
                        background: !animateBackground || isBackgroundTransitionEnded ? color : undefined,
                        width: setWidth(),
                        height: setHeight(),
                        minHeight: 60,
                        maxHeight: isMobile ? '100%' : `calc(100% - ${theme.thatsMe.MuiToolbar.minHeight}`
                    }
                }
            }
        >
            <Stack
                onTransitionEnd={animateBackground ? (e) => {

                    if(!isModalTransitionEnded || startTime.diff(moment(Date.now())) > 0) {
                        return;
                    }

                    setIsBackgroundTransitionEnded(true);
                    onBackgroundTransitionEnd && onBackgroundTransitionEnd();
                } : undefined}
                height={'100%'}
                width={'100%'}
                sx={{
                    backgroundSize: '100% 200%',
                    backgroundImage: animateBackground ? `linear-gradient(to bottom, transparent 50%, ${color} 50%)` : undefined,
                    ...defaultStyle,
                    backgroundPosition: !animateBackground || !isModalTransitionEnded || startTime.diff(moment(Date.now())) < 0 ? '0 0' : '0 -100%'
                }}
            >
                <Box
                    maxWidth={isMobile ? '100%' : modalFixedWidth ?? '400px'}
                    width={"100%"}
                    position={isModalScrollable ? 'fixed' : 'initial'}
                    zIndex={1}
                >
                    {hasProgress && typeof progress !== 'undefined' && <LinearProgress
                        variant={"determinate"}
                        value={progress}
                        sx={{
                            height: 12,
                            width: '100%',
                            borderRadius: isMobile ? '0' : '4px 4px 0 0',
                            padding: '6px', // To enforce the height
                            backgroundColor: theme.palette.thatsMe.surfacePrimary,
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: progressColor
                            }
                        }}/>
                    }
                    <Box pt={hasProgress ? 1 : 2.5}
                         paddingX={2.5}
                         display={"flex"}
                         justifyContent={"flex-end"}
                    >
                        <IconButton
                            aria-label="modal close button"
                            onClick={onClose}
                            className={"modal-close-button"}
                            style={closeIconStyles}
                        >
                            <Close
                                size={20}
                                fill={closeIconColor}
                            />
                        </IconButton>
                    </Box>
                </Box>
                <Box marginTop={isModalScrollable ? 5.25 : 0} className={"modal-content-wrapper"} {...modalContentWrapperStyle} >
                    {children}
                </Box>
            </Stack>
        </Dialog>
    );
}

export default ModalWrapper;